import * as React from 'react'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import ResetTemporaryPassword from '../../../../components/dialogs/ResetTemporaryPassword'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { ManagerState } from '../../../../features/types'
import AddManagerDialog from './AddManagerDialog'

interface Props {
    managersData: ManagerState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
    onResetPassword: (id: number) => void
    onDelete: (subject: number) => void
}

const ManagersTable: React.FC<Props> = ({
    managersData = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage,
    isFetching,
    setPage,
    setRowsPerPage,
    onDelete,
    onResetPassword,
}) => {
    const { formatMessage } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, rowsPerPage - managersData.length) : 0

    // const emptyRows =
    // page > 0 ? Math.max(0, (1 + page) * rowsPerPage - teachersData.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'temporaryPassword' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'email' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'role' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {managersData.length ? (
                        <TableBody>
                            {managersData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.lastName} {row.firstName}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.mobile}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ResetTemporaryPassword
                                            password={row.password}
                                            fullName={
                                                row.lastName +
                                                ' ' +
                                                row.firstName
                                            }
                                            isLoading={isFetching}
                                            confirmAction={() =>
                                                onResetPassword(
                                                    row.id as number,
                                                )
                                            }
                                        />
                                    </TableCell>

                                    <TableCell>{row.email}</TableCell>

                                    <TableCell>
                                        {formatMessage({
                                            id: row.role.toLowerCase(),
                                        })}
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <AddManagerDialog
                                            editMode={true}
                                            editManager={row}
                                        />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <ConfirmDelete
                                            confirmAction={() =>
                                                onDelete(row.id as number)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={7} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={7}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default ManagersTable
